import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const intervalRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.data?.count || 0);
    } catch {
      // Silent fail for polling
    }
  }, []);

  const fetchNotifications = useCallback(async (page = 1) => {
    try {
      const res = await api.get('/notifications', { params: { page, limit: 20 } });
      setNotifications(res.data.data || []);
      return res.data;
    } catch {
      return { data: [] };
    }
  }, []);

  const markRead = useCallback(async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silent fail
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // Silent fail
    }
  }, []);

  // Poll for unread count every 60 seconds when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    fetchUnreadCount();
    intervalRef.current = setInterval(fetchUnreadCount, 60000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAuthenticated, fetchUnreadCount]);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    fetchNotifications,
    fetchUnreadCount,
    markRead,
    markAllRead,
  }), [notifications, unreadCount, fetchNotifications, fetchUnreadCount, markRead, markAllRead]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    return {
      notifications: [],
      unreadCount: 0,
      fetchNotifications: () => Promise.resolve({ data: [] }),
      fetchUnreadCount: () => Promise.resolve(),
      markRead: () => Promise.resolve(),
      markAllRead: () => Promise.resolve(),
    };
  }
  return context;
}

export default NotificationContext;
